import type { UIMatch } from 'react-router-dom';

export type LibraryTab = 'types' | 'plantTypes' | 'scadaVocabulary' | 'plants';

export type VocabularyView = 'readings' | 'equipmentCodes';

export interface LibraryTabHandle {
  readonly libraryTab: LibraryTab;
}

export interface VocabularyViewHandle {
  readonly vocabularyView: VocabularyView;
}

export const libraryTab = (tab: LibraryTab): LibraryTabHandle => ({ libraryTab: tab });

export const vocabularyView = (view: VocabularyView): VocabularyViewHandle => ({ vocabularyView: view });

const LIBRARY_TABS: readonly LibraryTab[] = ['types', 'plantTypes', 'scadaVocabulary', 'plants'];

const VOCABULARY_VIEWS: readonly VocabularyView[] = ['readings', 'equipmentCodes'];

const fieldOf = (handle: unknown, field: string): unknown =>
  typeof handle === 'object' && handle !== null ? (handle as Readonly<Record<string, unknown>>)[field] : undefined;

export const isLibraryTab = (value: unknown): value is LibraryTab => LIBRARY_TABS.some((tab) => tab === value);

export const isVocabularyView = (value: unknown): value is VocabularyView => VOCABULARY_VIEWS.some((view) => view === value);

const lastOf = <T>(matches: readonly UIMatch[], read: (handle: unknown) => T | null): T | null =>
  matches.reduce<T | null>((found, match) => read(match.handle) ?? found, null);

export const libraryTabOf = (matches: readonly UIMatch[]): LibraryTab | null =>
  lastOf(matches, (handle) => {
    const tab = fieldOf(handle, 'libraryTab');
    return isLibraryTab(tab) ? tab : null;
  });

export const vocabularyViewOf = (matches: readonly UIMatch[]): VocabularyView | null =>
  lastOf(matches, (handle) => {
    const view = fieldOf(handle, 'vocabularyView');
    return isVocabularyView(view) ? view : null;
  });

export const leafPathnameOf = (matches: readonly UIMatch[]): string | null => matches[matches.length - 1]?.pathname ?? null;
